// Componente de formulario de suscripción con Mercado Pago
// Integra el servicio de Mercado Pago para procesar suscripciones

import React, { useState, useEffect } from 'react';
import { FiLoader, FiCreditCard, FiCheck, FiAlertCircle, FiX } from 'react-icons/fi';
import Button from '../../components/UI/Button';
import mercadoPagoService from '../../services/mercadopago_compatible';
import { ERROR_MESSAGES } from '../../constants/subscriptions';

/**
 * Obtiene el precio efectivo del plan (promocional si existe, sino el normal)
 * @param {Object} plan - Datos del plan
 * @returns {number} Precio efectivo a cobrar
 */
const getEffectivePrice = (plan) => {
  // Si existe precio promocional y es diferente de cero, usarlo
  if (plan.plan_price_promo !== null && plan.plan_price_promo !== undefined) {
    return plan.plan_price_promo;
  }
  // Sino usar el precio normal
  return plan.plan_price || plan.precio;
};

/**
 * Componente SubscriptionForm
 * 
 * Formulario para procesar suscripciones con Mercado Pago
 * Maneja el flujo completo: creación, redirección y confirmación
 */
const SubscriptionForm = ({ 
  plan, 
  user, 
  currentSubscription = null,
  onSuccess, 
  onError, 
  onCancel 
}) => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('form'); // form, processing, redirecting, success, error
  const [error, setError] = useState('');
  const [subscriptionData, setSubscriptionData] = useState(null);

  // Inicializar servicio de Mercado Pago
  useEffect(() => {
    // En producción, estas credenciales vendrían de variables de entorno
    // Por ahora se configuran hardcoded para desarrollo
    const accessToken = process.env.REACT_APP_MERCADO_PAGO_ACCESS_TOKEN || 'TEST_ACCESS_TOKEN';
    const publicKey = process.env.REACT_APP_MERCADO_PAGO_PUBLIC_KEY || 'TEST_PUBLIC_KEY';
    
    mercadoPagoService.initialize(accessToken, publicKey);
  }, []);

  /**
   * Maneja el envío del formulario de suscripción
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user || !plan) {
      setError('Faltan datos requeridos para procesar la suscripción');
      return;
    }

    setLoading(true);
    setError('');
    setStep('processing');

    try {
      // Verificar si el servicio está inicializado
      if (!mercadoPagoService.isInitialized()) {
        throw new Error('Servicio de Mercado Pago no inicializado');
      }

      // Preparar datos de suscripción
      const subscriptionRequest = {
        planId: plan.id,
        userId: user.id,
        email: user.email
      };

      // Crear suscripción con Mercado Pago
      const result = await mercadoPagoService.createSubscription(
        subscriptionRequest, 
        plan
      );

      if (!result.success) {
        throw new Error(result.error);
      }

      setSubscriptionData(result);
      setStep('redirecting');

      // Redirigir a Mercado Pago después de un breve delay
      setTimeout(() => {
        if (result.initPoint) {
          window.location.href = result.initPoint;
        } else {
          throw new Error('No se obtuvo URL de redirección de Mercado Pago');
        }
      }, 2000);

    } catch (error) {
      console.error('Error en suscripción:', error);
      setError(error.message || ERROR_MESSAGES.GENERIC_ERROR);
      setStep('error');
      setLoading(false);
      
      if (onError) {
        onError(error.message);
      }
    }
  };

  /**
   * Renderiza el paso actual del formulario
   */
  const renderStep = () => {
    switch (step) {
      case 'form':
        return (
          <div className="max-w-md mx-auto">
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-lg p-6">
              {/* Header */}
              <div className="text-center mb-6">
                <FiCreditCard className="h-12 w-12 text-primary mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {currentSubscription ? 'Cambiar de Plan' : 'Suscribirse a Plan'}
                </h2>
                <p className="text-gray-600 dark:text-gray-300">
                  {currentSubscription 
                    ? `Cambiarás del plan actual a "${plan.nombre}"`
                    : `Te suscribirás al plan "${plan.nombre}"`
                  }
                </p>
              </div>

              {/* Detalles del Plan */}
              <div className="bg-gray-50 dark:bg-neutral-700 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  {plan.nombre}
                </h3>
                <div className="flex items-baseline gap-2 mb-2">
                  {/* Mostrar precio promocional si existe */}
                  {(plan.plan_price_promo || plan.plan_price_promo === 0) ? (
                    <>
                      <span className="text-2xl font-bold text-green-600">
                        ${plan.plan_price_promo}
                      </span>
                      <span className="text-gray-500 line-through text-lg">
                        ${plan.plan_price || plan.precio}
                      </span>
                    </>
                  ) : (
                    <span className="text-2xl font-bold text-primary">
                      ${plan.plan_price || plan.precio}
                    </span>
                  )}
                  <span className="text-sm text-gray-600 dark:text-gray-300">/mes</span>
                </div>
                {plan.descripcion && (
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {plan.descripcion}
                  </p>
                )}
              </div>

              {/* Información del Usuario */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                  Información de Facturación
                </h4>
                <div className="bg-gray-50 dark:bg-neutral-700 rounded-lg p-3">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    <strong>Email:</strong> {user.email}
                  </p>
                  {user.name && (
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      <strong>Nombre:</strong> {user.name}
                    </p>
                  )}
                </div>
              </div>

              {/* Advertencia si es cambio de plan */}
              {currentSubscription && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <FiAlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-1">
                        Cambio de Plan
                      </h4>
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">
                        {getEffectivePrice(plan) > (currentSubscription.monto_mensual || 0)
                          ? 'Se aplicará un cargo prorrateado por la diferencia.'
                          : getEffectivePrice(plan) < (currentSubscription.monto_mensual || 0)
                          ? 'El cambio se aplicará en el próximo ciclo de facturación.'
                          : 'El cambio será efectivo inmediatamente.'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Botones de Acción */}
              <div className="flex gap-3">
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  loading={loading}
                  disabled={loading}
                  className="flex-1"
                >
                  {loading ? (
                    <>
                      <FiLoader className="animate-spin h-4 w-4 mr-2" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <FiCreditCard className="h-4 w-4 mr-2" />
                      {currentSubscription ? 'Cambiar Plan' : 'Suscribirse'}
                    </>
                  )}
                </Button>
                
                <Button
                  variant="ghost"
                  onClick={onCancel}
                  disabled={loading}
                  className="flex-1"
                >
                  Cancelar
                </Button>
              </div>

              {/* Información de Seguridad */}
              <div className="mt-6 text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  <FiCheck className="inline h-3 w-3 mr-1" />
                  Pago seguro procesado por Mercado Pago
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Podrás cancelar tu suscripción en cualquier momento
                </p>
              </div>
            </div>
          </div>
        );

      case 'processing':
        return (
          <div className="max-w-md mx-auto">
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-lg p-8 text-center">
              <FiLoader className="h-16 w-16 text-primary animate-spin mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Procesando Suscripción
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Estamos creando tu suscripción con Mercado Pago...
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Por favor espera, serás redirigido en unos segundos.
              </p>
            </div>
          </div>
        );

      case 'redirecting':
        return (
          <div className="max-w-md mx-auto">
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-lg p-8 text-center">
              <FiCreditCard className="h-16 w-16 text-primary mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Redirigiendo a Mercado Pago
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Serás redirigido a la página de pago segura de Mercado Pago.
              </p>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>Importante:</strong> Completa el pago en Mercado Pago para activar tu suscripción.
                </p>
              </div>
              <div className="mt-4">
                <div className="animate-pulse">
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full w-full"></div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'success':
        return (
          <div className="max-w-md mx-auto">
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-lg p-8 text-center">
              <FiCheck className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                ¡Suscripción Exitosa!
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Tu suscripción ha sido procesada exitosamente.
              </p>
              <Button
                variant="primary"
                onClick={() => {
                  if (onSuccess) {
                    onSuccess(subscriptionData);
                  }
                }}
              >
                Continuar
              </Button>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="max-w-md mx-auto">
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-lg p-8 text-center">
              <FiX className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Error en la Suscripción
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                {error || 'Ocurrió un error procesando tu suscripción.'}
              </p>
              <div className="flex gap-3">
                <Button
                  variant="primary"
                  onClick={() => {
                    setStep('form');
                    setError('');
                  }}
                >
                  Reintentar
                </Button>
                <Button
                  variant="ghost"
                  onClick={onCancel}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-900 flex items-center justify-center p-4">
      {renderStep()}
    </div>
  );
};

export default SubscriptionForm;
